import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { api } from '../api/client'
import type { BusinessApp, BusinessAppElement, BusinessAppFunction, Criticita } from '../api/types'
import { CriticitaBadge, SceltaCriticita, useAmministra } from '../components/BusinessApps'
import { Card, EmptyState, ErrorBanner, Spinner, StatCard } from '../components/common'
import { DataTable, type Column } from '../components/DataTable'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'
import { BottoniEsporta } from '../components/BottoniEsporta'
import { CRITICALITY_LABEL } from './Services'

/**
 * A Business App: its elements (programs on CIs, from the Mappa Software),
 * its functions with their criticality and the elements implementing each,
 * and the business services using it — which inherit the highest criticality
 * of the functions. See business_app_service.
 */
export default function BusinessAppDetail() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const admin = useAmministra()
  const dati = useAsync(() => api.businessApp(id), [id])
  const servizi = useAsync(() => api.services(), [])
  const [errore, setErrore] = useState<unknown>(null)
  const [modifica, setModifica] = useState(false)
  // The functions whose elements are being chosen (the panel under the row).
  const [funzioniAperte, setFunzioniAperte] = useState<Set<string>>(new Set())
  const alternaFunzione = (fid: string) => setFunzioniAperte((prima) => {
    const dopo = new Set(prima)
    if (dopo.has(fid)) dopo.delete(fid)
    else dopo.add(fid)
    return dopo
  })

  if (dati.loading && !dati.data) return <Spinner />
  if (!dati.data) return <ErrorBanner error={dati.error} />
  const app = dati.data

  async function esegui(fn: () => Promise<unknown>) {
    setErrore(null)
    try {
      await fn()
      dati.reload()
    } catch (e) { setErrore(e) }
  }

  const nomeElemento = new Map(app.elements.map((e) => [e.id, `${e.name || e.software_key} · ${e.ci_name ?? ''}`]))
  const realizzataDa = (f: BusinessAppFunction) => f.element_ids.map((e) => nomeElemento.get(e)).filter(Boolean).join(', ')

  const colonneFunzioni: Column<BusinessAppFunction>[] = [
    {
      key: 'name',
      header: t('Funzionalità'),
      value: (f) => `${f.name}${f.description ? ` ${f.description}` : ''}`,
      render: (f) => <><strong>{f.name}</strong>{f.description && <><br /><small className="text-muted">{f.description}</small></>}</>,
    },
    {
      key: 'criticality',
      header: t('Criticità'),
      value: (f) => CRITICALITY_LABEL[f.criticality] ?? f.criticality,
      render: (f) => (admin
        ? <SceltaCriticita value={f.criticality} onChange={(v) => void esegui(() => api.updateBusinessAppFunction(app.id, f.id, { criticality: v }))} />
        : <CriticitaBadge value={f.criticality} />),
    },
    { key: 'elements', header: t('Realizzata da'), value: realizzataDa, render: (f) => <small>{realizzataDa(f) || '—'}</small> },
    ...(admin
      ? [{
          key: 'azioni',
          header: '',
          sort: false as const,
          filter: false as const,
          searchable: false,
          nowrap: true,
          render: (f: BusinessAppFunction) => (
            <>
              <button className="link" onClick={() => alternaFunzione(f.id)}>{t('Elementi')}</button>{' · '}
              <button className="link" onClick={() => {
                if (window.confirm(t('Eliminare la funzionalità «{nome}»?', { nome: f.name }))) void esegui(() => api.deleteBusinessAppFunction(app.id, f.id))
              }}>{t('Elimina')}</button>
            </>
          ),
        } as Column<BusinessAppFunction>]
      : []),
  ]

  const colonneElementi: Column<BusinessAppElement>[] = [
    {
      key: 'name',
      header: t('Programma'),
      value: (e) => `${e.name || e.software_key}${e.process && e.process !== e.name ? ` · ${e.process}` : ''}`,
      render: (e) => <>{e.name || e.software_key}{e.process && e.process !== e.name && <small className="text-muted"> · {e.process}</small>}</>,
    },
    {
      key: 'ci_name',
      header: t('CI'),
      value: (e) => `${e.ci_name ?? e.ci_id}${e.ci_class ? ` · ${e.ci_class}` : ''}`,
      render: (e) => <><Link to={`/cis/${e.ci_id}`}>{e.ci_name ?? e.ci_id}</Link>{e.ci_class && <small className="text-muted"> · {e.ci_class}</small>}</>,
    },
    { key: 'ports', header: t('Porte'), mono: true, value: (e) => e.ports.join(', ') },
    { key: 'kind', header: t('Tipo'), render: (e) => <small>{e.kind || '—'}</small> },
    ...(admin
      ? [{
          key: 'azioni',
          header: '',
          sort: false as const,
          filter: false as const,
          searchable: false,
          render: (e: BusinessAppElement) => (
            <button className="link" onClick={() => { if (window.confirm(t('Togliere questo elemento dalla Business App? Non si può annullare.'))) esegui(() => api.removeBusinessAppElement(app.id, e.id)) }}>{t('Togli')}</button>
          ),
        } as Column<BusinessAppElement>]
      : []),
  ]

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{app.name}</h1>
          <p className="page__subtitle">
            <CriticitaBadge value={app.criticality} />
            {app.owner && <> · {app.owner}</>} {app.description && <> · {app.description}</>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <BottoniEsporta path={(fmt) => `/business-apps/${app.id}/export?fmt=${fmt}`} nome={`censo-business-app-${app.name}`} />
          <Link to="/business-apps">{t('← Torna alle Business App')}</Link>
        </div>
      </header>
      <ErrorBanner error={errore} />

      <div className="stats">
        <StatCard label={t('Funzionalità')} value={app.functions.length} />
        <StatCard label={t('Elementi')} value={app.elements.length}
          hint={t('su {n} CI', { n: new Set(app.elements.map((e) => e.ci_id)).size })} />
        <StatCard label={t('Business service')} value={app.services.length} />
      </div>

      {admin && (
        <div className="toolbar" style={{ marginBottom: 16 }}>
          <span style={{ flex: 1 }} />
          <button className="secondary" onClick={() => setModifica((v) => !v)}>{modifica ? t('Annulla') : t('Modifica')}</button>
          <button className="danger" onClick={() => {
            if (!window.confirm(t('Eliminare la Business App «{nome}»?', { nome: app.name }))) return
            void api.deleteBusinessApp(app.id).then(() => navigate('/business-apps')).catch(setErrore)
          }}>{t('Elimina')}</button>
        </div>
      )}
      {modifica && <ModificaApp app={app} onFatto={() => { setModifica(false); dati.reload() }} />}

      <Card title={t('Funzionalità ({n})', { n: app.functions.length })}>
        <p className="field__hint" style={{ marginTop: 0 }}>
          {t('Che cosa fa l’applicazione per chi la usa, ciascuna con la sua criticità. La criticità dell’app è la più alta fra le sue funzionalità e passa ai Business Service che la usano.')}
        </p>
        {app.functions.length === 0 ? <p className="text-muted">{t('Nessuna funzionalità ancora.')}</p> : (
          <DataTable
            rows={app.functions}
            columns={colonneFunzioni}
            rowKey={(f) => f.id}
            conteggio={{ nome: t('funzionalità') }}
            expand={(f) => (
              <ElementiFunzione app={app} f={f} nomeElemento={nomeElemento} esegui={esegui}
                onSalvata={() => alternaFunzione(f.id)} />
            )}
            expanded={(f) => funzioniAperte.has(f.id)}
          />
        )}
        {admin && <NuovaFunzione app={app} esegui={esegui} />}
      </Card>

      <Card title={t('Elementi ({n})', { n: app.elements.length })}>
        <p className="field__hint" style={{ marginTop: 0 }}>
          {t('I programmi che compongono l’app, sui loro CI. Si aggiungono dalla Mappa Software.')}{' '}
          <Link to={app.elements[0] ? `/software-flow?ci=${app.elements[0].ci_id}` : '/software-flow'}>{t('Apri la Mappa Software')}</Link>
        </p>
        {app.elements.length === 0 ? (
          <EmptyState title={t('Nessun elemento')} hint={t('Sulla Mappa Software si sceglie un CI, si cliccano i programmi e si aggiungono a questa app.')} />
        ) : (
          <DataTable
            rows={app.elements}
            columns={colonneElementi}
            rowKey={(e) => e.id}
            conteggio={{ nome: t('elementi') }}
          />
        )}
      </Card>

      <Card title={t('Business service che la usano ({n})', { n: app.services.length })}>
        <p className="field__hint" style={{ marginTop: 0 }}>
          {t('Ognuno eredita la criticità dell’app se è più alta di quella dichiarata, e i CI degli elementi diventano suoi componenti.')}
        </p>
        {admin ? (
          <SceltaServizi app={app} servizi={(servizi.data ?? []).map((s) => ({ id: s.id, name: s.name }))} esegui={esegui} />
        ) : app.services.length === 0 ? <p className="text-muted">—</p> : (
          <ul>{app.services.map((s) => <li key={s.id}><Link to={`/services/${s.id}`}>{s.name}</Link></li>)}</ul>
        )}
      </Card>
    </>
  )
}

/** The elements a function is made of, ticked under its row. */
function ElementiFunzione({ app, f, nomeElemento, esegui, onSalvata }: {
  app: BusinessApp; f: BusinessAppFunction; nomeElemento: Map<string, string>
  esegui: (fn: () => Promise<unknown>) => Promise<void>
  onSalvata: () => void
}) {
  const [scelti, setScelti] = useState<Set<string>>(new Set(f.element_ids))
  return (
    <>
      {app.elements.length === 0 ? <small className="text-muted">{t('L’app non ha ancora elementi.')}</small> : app.elements.map((e) => (
        <label key={e.id} className="checkbox" style={{ marginRight: 16 }}>
          <input type="checkbox" checked={scelti.has(e.id)} onChange={() => {
            const next = new Set(scelti)
            if (next.has(e.id)) next.delete(e.id)
            else next.add(e.id)
            setScelti(next)
          }} /> {nomeElemento.get(e.id)}
        </label>
      ))}
      <button style={{ marginLeft: 8 }} onClick={() => void esegui(() => api.updateBusinessAppFunction(app.id, f.id, { element_ids: [...scelti] })).then(onSalvata)}>
        {t('Salva')}
      </button>
    </>
  )
}

function NuovaFunzione({ app, esegui }: { app: BusinessApp; esegui: (fn: () => Promise<unknown>) => Promise<void> }) {
  const [nome, setNome] = useState('')
  const [descrizione, setDescrizione] = useState('')
  const [criticita, setCriticita] = useState<Criticita>('medium')
  return (
    <div className="toolbar" style={{ flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
      <input value={nome} onChange={(e) => setNome(e.target.value)} placeholder={t('Nuova funzionalità (es. Emissione fatture)')} />
      <input value={descrizione} onChange={(e) => setDescrizione(e.target.value)} placeholder={t('Descrizione (facoltativa)')} />
      <SceltaCriticita value={criticita} onChange={setCriticita} />
      <button disabled={!nome.trim()} onClick={() => void esegui(() => api.addBusinessAppFunction(app.id, {
        name: nome.trim(), criticality: criticita, description: descrizione.trim() || null,
      })).then(() => { setNome(''); setDescrizione('') })}>{t('Aggiungi funzionalità')}</button>
    </div>
  )
}

function SceltaServizi({ app, servizi, esegui }: {
  app: BusinessApp; servizi: Array<{ id: string; name: string }>; esegui: (fn: () => Promise<unknown>) => Promise<void>
}) {
  const attuali = new Set(app.services.map((s) => s.id))
  if (servizi.length === 0) return <p className="text-muted">{t('Non ci sono ancora Business Service.')} <Link to="/services">{t('Crea un servizio')}</Link></p>
  return (
    <div>
      {servizi.map((s) => (
        <label key={s.id} className="checkbox" style={{ display: 'block' }}>
          <input type="checkbox" checked={attuali.has(s.id)} onChange={() => {
            const next = new Set(attuali)
            if (next.has(s.id)) next.delete(s.id)
            else next.add(s.id)
            void esegui(() => api.setBusinessAppServices(app.id, [...next]))
          }} /> <Link to={`/services/${s.id}`}>{s.name}</Link>
          {app.services.find((x) => x.id === s.id) && <small className="text-muted"> · {t('criticità effettiva')}: <CriticitaBadge value={app.services.find((x) => x.id === s.id)?.criticality} /></small>}
        </label>
      ))}
    </div>
  )
}

function ModificaApp({ app, onFatto }: { app: BusinessApp; onFatto: () => void }) {
  const [nome, setNome] = useState(app.name)
  const [descrizione, setDescrizione] = useState(app.description ?? '')
  const [responsabile, setResponsabile] = useState(app.owner ?? '')
  const [errore, setErrore] = useState<unknown>(null)
  return (
    <Card>
      <ErrorBanner error={errore} />
      <div className="form-grid">
        <label className="field"><span className="field__label">{t('Nome')}</span><input value={nome} onChange={(e) => setNome(e.target.value)} /></label>
        <label className="field"><span className="field__label">{t('Responsabile')}</span><input value={responsabile} onChange={(e) => setResponsabile(e.target.value)} /></label>
        <label className="field"><span className="field__label">{t('Descrizione')}</span><input value={descrizione} onChange={(e) => setDescrizione(e.target.value)} /></label>
      </div>
      <button onClick={async () => {
        try {
          await api.updateBusinessApp(app.id, { name: nome, description: descrizione || null, owner: responsabile || null })
          onFatto()
        } catch (e) { setErrore(e) }
      }}>{t('Salva')}</button>
    </Card>
  )
}
